import React, { useEffect, useId, useRef, useState } from 'react';
import { Building2, Check, ChevronUp, Loader2 } from 'lucide-react';

/**
 * Sélecteur de contexte A3 (ACCESS-CONTROL-STANDARD §5) — wayfinding corporate.
 * Visible UNIQUEMENT quand l'app passe la prop (admin corporate). Pose le
 * `company_id` de session ; n'accorde RIEN. @gs/ui n'utilise jamais `t()` :
 * libellés via props (défauts FR).
 */
export interface GsContextSwitcherProps {
  /** company_id du contexte courant (home ou bascule). */
  currentCompanyId: string;
  /** Compagnies commutables (résolues serveur — jamais fournies par le client). */
  companies: { companyId: string; companyName: string; companyCode: string }[];
  /** Bascule vers la compagnie choisie (appelle setContext côté app). */
  onSelect: (companyId: string) => void;
  /** Libellé de section / aria (défaut FR « Contexte »). */
  label?: string;
  /** Texte affiché quand le contexte courant n'est pas une compagnie commutable (home/god). */
  homeOptionLabel?: string;
  /** Désactive le sélecteur pendant la bascule (évite le double-clic). */
  busy?: boolean;
}

export interface GsContextSwitcherViewProps extends GsContextSwitcherProps {
  /** Sidebar rétractée — trigger icône seule + menu à droite. */
  collapsed?: boolean;
  variant?: 'navy' | 'light';
}

/**
 * Affichage compact de la compagnie courante + menu de bascule au clic.
 * Navigation / wayfinding (accent brand léger sur l'item actif) — jamais un aplat.
 */
export function GsContextSwitcher({
  currentCompanyId,
  companies,
  onSelect,
  label = 'Contexte',
  homeOptionLabel = 'Universal Group (corporate)',
  busy = false,
  collapsed = false,
  variant = 'light',
}: GsContextSwitcherViewProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const isLight = variant === 'light';

  const currentIsSwitchable = companies.some((c) => c.companyId === currentCompanyId);
  const currentCompany = companies.find((c) => c.companyId === currentCompanyId);
  const displayName = currentIsSwitchable
    ? (currentCompany?.companyName ?? homeOptionLabel)
    : homeOptionLabel;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  useEffect(() => {
    if (busy) setOpen(false);
  }, [busy]);

  const nameTone = isLight ? 'text-slate-700 dark:text-slate-200' : 'text-slate-200';
  const mutedTone = isLight ? 'text-slate-400 dark:text-slate-500' : 'text-slate-500';
  const menuBg = isLight
    ? 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700'
    : 'bg-slate-800 border border-white/10';
  const menuText = isLight ? 'text-slate-700 dark:text-slate-200' : 'text-slate-200';
  const menuHover = isLight ? 'hover:bg-slate-50 dark:hover:bg-slate-800' : 'hover:bg-white/10';
  const menuHeader = isLight
    ? 'text-slate-400 dark:text-slate-500'
    : 'text-slate-500';

  const handleSelect = (companyId: string) => {
    if (busy || companyId === currentCompanyId) {
      setOpen(false);
      return;
    }
    setOpen(false);
    onSelect(companyId);
  };

  const triggerClass = [
    'flex w-full items-center transition-colors touch-manipulation',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 focus-visible:ring-inset',
    isLight
      ? 'hover:bg-slate-50 dark:hover:bg-slate-800/80'
      : 'hover:bg-white/5',
    busy ? 'cursor-wait opacity-70' : '',
    collapsed ? 'justify-center px-0 py-2.5' : 'gap-2 px-3 py-2',
  ].join(' ');

  return (
    <div
      ref={containerRef}
      className="relative"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        data-tour="context-switcher"
        disabled={busy}
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`${label} — ${displayName}`}
        title={collapsed ? displayName : undefined}
        className={triggerClass}
      >
        {busy ? (
          <Loader2 size={15} className={`shrink-0 animate-spin text-brand-500`} aria-hidden />
        ) : (
          <Building2 size={15} className={`shrink-0 opacity-70 ${mutedTone}`} aria-hidden />
        )}
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1 text-left">
              <span className={`block truncate text-chip font-semibold uppercase tracking-[0.12em] ${mutedTone}`}>
                {label}
              </span>
              <span className={`block truncate text-meta font-medium ${nameTone}`}>
                {displayName}
              </span>
            </span>
            <ChevronUp
              size={13}
              className={`shrink-0 transition-transform ${mutedTone} ${open ? 'rotate-0' : 'rotate-180'}`}
              aria-hidden
            />
          </>
        )}
      </button>

      {open && !busy && (
        <div
          id={menuId}
          role="menu"
          aria-label={label}
          className={[
            'absolute z-50 overflow-hidden rounded-xl shadow-xl',
            collapsed
              ? 'left-full bottom-0 right-auto ml-2 mb-0 w-56'
              : 'bottom-full left-0 right-0 mb-1',
            menuBg,
          ].join(' ')}
        >
          <div className={`px-3 py-2 text-chip font-semibold uppercase tracking-[0.12em] ${menuHeader}`}>
            {label}
          </div>

          {!currentIsSwitchable && (
            <div
              role="menuitem"
              aria-current="true"
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-meta ${menuText}`}
            >
              <Check size={14} className="shrink-0 text-brand-500" aria-hidden />
              <span className="min-w-0 flex-1 truncate font-medium text-brand-500">
                {homeOptionLabel}
              </span>
            </div>
          )}

          {companies.map((c) => {
            const selected = c.companyId === currentCompanyId;
            return (
              <button
                key={c.companyId}
                type="button"
                role="menuitem"
                aria-current={selected ? 'true' : undefined}
                disabled={busy}
                onClick={() => handleSelect(c.companyId)}
                className={[
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-meta transition-colors touch-manipulation',
                  menuHover,
                  selected ? 'font-medium text-brand-500' : menuText,
                ].join(' ')}
              >
                <span className="flex w-3.5 shrink-0 justify-center" aria-hidden>
                  {selected ? <Check size={14} className="text-brand-500" /> : null}
                </span>
                <span className="min-w-0 flex-1 truncate">{c.companyName}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
